// @ts-nocheck
// Tela full-screen pra explorar clubes públicos disponíveis no Forjamente.
// Filtros: busca por nome + modo (leitura/comunidade) + ordenação (recentes/populares).
// Click num card abre o detalhe do clube — onde user pode entrar (privacy=open
// permite entrada direta, sem aprovação de admin).
import React, { useState, useEffect } from 'react';
import { Icon } from '../icons';
import { BookCover } from '../common';
import { useTerm, UsersIcon } from './components';
import { api } from '../../services/apiV2';

const SORT_OPTIONS = [
  { id: 'recent',  label: 'Recentes' },
  { id: 'popular', label: 'Populares' },
] as const;

const MODE_OPTIONS = [
  { id: '',             label: 'Todos' },
  { id: 'group-read',   label: '📖 Leitura' },
  { id: 'shared-habit', label: '🌱 Hábito' },
] as const;

export const ClubsExploreScreen: React.FC<{
  term?: 'clube' | 'grupo';
  session?: any;
  onBack: () => void;
  onOpenClub?: (clubId: string) => void;
  // Quando o user é free, esse callback abre o paywall do app
  onOpenPaywall?: () => void;
}> = ({ term = 'clube', session, onBack, onOpenClub, onOpenPaywall }) => {
  const T = useTerm(term);
  const [clubs, setClubs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [mode, setMode] = useState<string>('');
  const [sort, setSort] = useState<'recent' | 'popular'>('recent');
  const [planLocked, setPlanLocked] = useState(false);

  // Debounce da busca: 300ms
  useEffect(() => {
    if (!session) return;
    // `cancelled` VIVE NO EFEITO, nao dentro do setTimeout.
    //
    // Estava declarado la dentro, com um `return () => { cancelled = true }` no
    // fim do callback — e o retorno de uma funcao passada ao setTimeout e
    // JOGADO FORA pelo JS. Ninguem chamava aquele cleanup, entao `cancelled`
    // nunca virava true: a resposta de uma busca antiga, chegando depois da
    // nova, sobrescrevia a tela com clubes que nao tinham nada a ver com o que
    // estava escrito na caixa. Em 4G instavel isso acontece de verdade.
    //
    // O unico cleanup que o efeito registrava era o `clearTimeout`, que so
    // ajuda enquanto o pedido ainda nao saiu.
    let cancelled = false;
    const t = setTimeout(() => {
      setLoading(true);
      setError(null);
      (async () => {
        try {
          const r = await api.exploreClubs(session, { q, mode, sort, limit: 50 });
          if (!cancelled) setClubs(r.clubs || []);
        } catch (e: any) {
          if (cancelled) return;
          // Plano insuficiente: backend retorna 402 com 'plan_required'
          if (e?.message?.includes('plan_required')) {
            setPlanLocked(true);
          } else {
            setError(e?.message || 'Erro ao carregar');
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [session, q, mode, sort]);

  return (
    <div className="scroll" style={{ paddingBottom: 110 }}>
      {/* Header */}
      <div style={{
        padding: '54px 20px 16px',
        position: 'sticky', top: 0, zIndex: 10,
        background: 'var(--paper)',
        borderBottom: '1px solid var(--line)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={onBack} style={{
            width: 36, height: 36, borderRadius: '50%',
            background: 'var(--paper-2)', border: '1px solid var(--line)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Icon name="chevron-left" size={16}/>
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="mono" style={{
              fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase',
              color: 'var(--ink-3)',
            }}>EXPLORAR</div>
            <div className="serif" style={{ fontSize: 26, lineHeight: 1.05, marginTop: 2, letterSpacing: '-0.005em' }}>
              <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>{T.P}</span> abertos
            </div>
          </div>
        </div>

        {/* Busca */}
        <div style={{ marginTop: 14, position: 'relative' }}>
          <div style={{
            position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            display: 'flex', alignItems: 'center',
          }}>
            <Icon name="search" size={14} color="var(--ink-3)"/>
          </div>
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder={`Buscar ${T.s} por nome...`}
            style={{
              width: '100%', padding: '10px 14px 10px 38px', borderRadius: 12,
              background: 'var(--paper-2)', border: '1px solid var(--line)',
              fontSize: 14, outline: 'none', fontFamily: 'inherit', color: 'var(--ink)',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Filtros */}
        <div style={{
          marginTop: 10, display: 'flex', gap: 6, overflowX: 'auto',
          marginLeft: -20, marginRight: -20, paddingLeft: 20, paddingRight: 20,
        }}>
          {MODE_OPTIONS.map(m => {
            const sel = mode === m.id;
            return (
              <button key={m.id || 'all'} onClick={() => setMode(m.id)} style={{
                flexShrink: 0,
                padding: '6px 12px', borderRadius: 999,
                background: sel ? 'var(--ink)' : 'var(--paper-2)',
                color: sel ? 'var(--paper)' : 'var(--ink-2)',
                border: sel ? '1px solid var(--ink)' : '1px solid var(--line-strong)',
                fontSize: 12, fontWeight: sel ? 500 : 400,
                cursor: 'pointer',
              }}>{m.label}</button>
            );
          })}
          <span style={{ width: 8, flexShrink: 0 }}/>
          {SORT_OPTIONS.map(s => {
            const sel = sort === s.id;
            return (
              <button key={s.id} onClick={() => setSort(s.id as any)} style={{
                flexShrink: 0,
                padding: '6px 12px', borderRadius: 999,
                background: sel ? 'var(--ember-soft)' : 'transparent',
                color: sel ? 'var(--ember)' : 'var(--ink-3)',
                border: sel ? '1px solid var(--ember)' : '1px dashed var(--line-strong)',
                fontSize: 12, fontWeight: sel ? 600 : 400,
                cursor: 'pointer',
              }}>{s.label}</button>
            );
          })}
        </div>
      </div>

      {/* Conteúdo */}
      <div style={{ padding: '20px' }}>
        {planLocked && (
          <div style={{
            padding: 24, textAlign: 'center', borderRadius: 16,
            background: 'linear-gradient(135deg, var(--ember-soft), var(--paper-2))',
            border: '1px solid var(--ember)',
          }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🔒</div>
            <div className="serif" style={{ fontSize: 20, lineHeight: 1.1, marginBottom: 6 }}>
              Explorar é parte do <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>Reader</span>
            </div>
            <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.5, marginBottom: 16 }}>
              Atualize seu plano pra descobrir {T.p} públicos da comunidade Forjamente.
            </div>
            {onOpenPaywall && (
              <button onClick={onOpenPaywall} style={{
                padding: '12px 22px', borderRadius: 999,
                background: 'var(--ember)', color: 'var(--paper)',
                border: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                boxShadow: '0 8px 18px -6px rgba(199,83,26,0.4)',
              }}>Ver planos</button>
            )}
          </div>
        )}

        {error && !planLocked && (
          <div style={{
            padding: 14, borderRadius: 12,
            background: 'var(--ember-soft)', color: 'var(--ember)',
            fontSize: 13,
          }}>{error}</div>
        )}

        {!planLocked && loading && clubs.length === 0 && (
          <div style={{ padding: '40px 16px', textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
            Carregando…
          </div>
        )}

        {!planLocked && !loading && clubs.length === 0 && (
          <div style={{
            padding: 30, textAlign: 'center',
            border: '1px dashed var(--line-strong)', borderRadius: 16,
            color: 'var(--ink-3)',
          }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🔍</div>
            <div className="serif" style={{ fontSize: 18, fontStyle: 'italic', marginBottom: 4 }}>
              Nenhum {T.s} encontrado
            </div>
            <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>
              {q
                ? <>Tente outra busca ou remova os filtros.</>
                : <>Ainda não há {T.p} abertos com esses critérios. Que tal <strong style={{ color: 'var(--ember)' }}>criar o seu</strong>?</>}
            </div>
          </div>
        )}

        {!planLocked && clubs.length > 0 && (
          <>
            <div className="mono" style={{
              fontSize: 9.5, letterSpacing: '0.12em', textTransform: 'uppercase',
              color: 'var(--ink-3)', marginBottom: 12,
            }}>
              {clubs.length} {clubs.length === 1 ? 'resultado' : 'resultados'}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {clubs.map(c => (
                <ExploreClubCard key={c.id} club={c} term={term} onClick={() => onOpenClub?.(c.id)}/>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const ExploreClubCard: React.FC<{
  club: any; term: 'clube' | 'grupo'; onClick?: () => void;
}> = ({ club, term, onClick }) => {
  const T = useTerm(term);
  const isGroupRead = club.mode === 'group-read';
  return (
    <div onClick={onClick} style={{
      padding: 14, borderRadius: 16,
      background: 'var(--paper-2)', border: '1px solid var(--line)',
      display: 'flex', gap: 14, cursor: 'pointer',
      transition: 'border-color 160ms',
    }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--ember)'; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--line)'; }}
    >
      <div style={{ flexShrink: 0 }}>
        {isGroupRead && club.book ? (
          <BookCover
            book={{
              title: club.book.title,
              author: club.book.author,
              coverHue: club.book.coverHue,
              coverStyle: club.book.coverTemplate,
              coverUrl: club.book.coverUrl,
            } as any}
            width={64} height={94}
          />
        ) : (
          <div style={{
            width: 64, height: 94, borderRadius: 6,
            background: `linear-gradient(135deg, oklch(0.78 0.09 ${club.hue || 22}), oklch(0.45 0.10 ${club.hue || 22}))`,
            boxShadow: '0 12px 22px -10px rgba(0,0,0,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <UsersIcon size={26} color="rgba(255,255,255,0.85)"/>
          </div>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
            <span className="mono" style={{
              fontSize: 8.5, padding: '2px 7px', borderRadius: 999,
              background: 'var(--ember-soft)', color: 'var(--ember)',
              letterSpacing: '0.10em', textTransform: 'uppercase', fontWeight: 600,
            }}>{club.tag || 'Aberto'}</span>
            {/* Badge "Dono" / "Membro" — quando o user já participa desse clube.
               Owner tem prioridade sobre membership comum. */}
            {club.yourRole === 'owner' && (
              <span className="mono" style={{
                fontSize: 8.5, padding: '2px 7px', borderRadius: 999,
                background: 'rgba(199,83,26,0.18)', color: 'var(--ember)',
                letterSpacing: '0.10em', textTransform: 'uppercase', fontWeight: 700,
                border: '1px solid rgba(199,83,26,0.35)',
              }}>★ Dono</span>
            )}
            {club.yourRole && club.yourRole !== 'owner' && (
              <span className="mono" style={{
                fontSize: 8.5, padding: '2px 7px', borderRadius: 999,
                background: 'rgba(74,138,90,0.16)', color: '#4A8A5A',
                letterSpacing: '0.10em', textTransform: 'uppercase', fontWeight: 600,
                border: '1px solid rgba(74,138,90,0.30)',
              }}>✓ Membro</span>
            )}
          </div>
          <div className="serif" style={{
            fontSize: 17, lineHeight: 1.15, color: 'var(--ink)',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{club.name}</div>
          {club.book && (
            <div style={{
              fontSize: 12, color: 'var(--ink-3)', marginTop: 4,
              fontStyle: 'italic',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              "{club.book.title}"{club.book.author ? ` — ${club.book.author}` : ''}
            </div>
          )}
          {!club.book && club.description && (
            <div style={{
              fontSize: 12, color: 'var(--ink-3)', marginTop: 4, lineHeight: 1.4,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{club.description}</div>
          )}
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginTop: 8,
          fontSize: 11, color: 'var(--ink-3)',
        }}>
          <UsersIcon size={11} color="var(--ink-3)"/>
          <strong style={{ color: 'var(--ink-2)', fontWeight: 600 }}>{club.members || 0}</strong>
          <span>{(club.members || 0) === 1 ? 'membro' : 'membros'}</span>
        </div>
      </div>
    </div>
  );
};
